import { createPet, deletePet, getPets, updatePet } from 'api/pets';
import { COOKIE_NAMES } from 'constants/cookies';
import { PetType } from 'constants/pet-type';
import omit from 'lodash/omit';
import unionBy from 'lodash/unionBy';
import { INIT_CUSTOMER_ID } from 'stores/auth';
import { useCookieStore } from 'stores/cookie';
import {
  DEFAULT_CUSTOMER_ID,
  INITIAL_CAT_STATE,
  INITIAL_DOG_STATE,
} from 'stores/pet-profile-new/constants';
import { PetProfileSlice, RootStore } from 'stores/pet-profile-new/types';
import { PetProfile } from 'types/pet-profile';
import { fromPetToProfile, toPayloadByPetProfile } from 'utils/pet-profile';
import { normalizePetProfileProteinPreferences } from 'utils/pet-profile/protein-preferences';
import { StateCreator } from 'zustand';
import { getCustomerKey } from './utils/get-customer-key';
import { mergePetProfiles } from './utils/merge-pet-profiles';
import { reconcileActivePetProfile } from './utils/reconcile-active-pet-profile';

export { DEFAULT_CUSTOMER_ID };

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

// empty pet profiles with preselected food types
export const INIT_PROFILES = [INITIAL_DOG_STATE, INITIAL_CAT_STATE];

const initialPetProfilesState: Pick<
  PetProfileSlice,
  'activePetProfile' | 'petProfiles' | 'profilesPerCustomer'
> = {
  activePetProfile: {
    [PetType.Cat]: INITIAL_CAT_STATE.id,
    [PetType.Dog]: INITIAL_DOG_STATE.id,
  },
  petProfiles: [],
  profilesPerCustomer: {
    [getCustomerKey(INIT_CUSTOMER_ID)]: [],
  },
};

export const petProfilesSlice: StateCreator<RootStore, [], [], PetProfileSlice> = (set, get) => ({
  ...initialPetProfilesState,
  customerPets: [],
  currentPetProfile: INITIAL_DOG_STATE,
  isCustomerPetsLoaded: false,
  _hasHydrated: false,
  cookieDomain: undefined,

  setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),

  setCookieDomain: (cookieDomain) => {
    set({ cookieDomain });
    // if pets are already known-loaded, re-sync so the cookies adopt the new domain immediately
    get().syncProfileCookies();
  },

  syncProfileCookies: () => {
    if (typeof window === 'undefined') return;

    const { customerPets, isCustomerPetsLoaded, cookieDomain } = get();
    // gate on known-loaded so we don't clobber prior-session cookies during the initial-mount hydration race
    if (!isCustomerPetsLoaded) return;

    const hasDog = customerPets.some((pet) => pet.petType === PetType.Dog);
    const hasCat = customerPets.some((pet) => pet.petType === PetType.Cat);

    const baseOptions = { domain: cookieDomain, path: '/', secure: true } as const;
    const persistedCookieOptions = { ...baseOptions, expires: new Date(Date.now() + ONE_YEAR_MS) };
    const expiredCookieOptions = { ...baseOptions, expires: new Date(0) };

    const { updateCookie } = useCookieStore.getState();
    updateCookie(
      COOKIE_NAMES.hasDogProfile,
      hasDog ? 'true' : '',
      hasDog ? persistedCookieOptions : expiredCookieOptions
    );
    updateCookie(
      COOKIE_NAMES.hasCatProfile,
      hasCat ? 'true' : '',
      hasCat ? persistedCookieOptions : expiredCookieOptions
    );
  },

  selectPetProfile: ({ petProfileId }: { petProfileId: string }) => {
    const { customerPets } = get();
    const targetProfile = customerPets.find((pet) => pet.id === petProfileId);
    if (!targetProfile) {
      console.error(`could not selectPetProfile with such id ${petProfileId}`);
      return;
    }

    set((store) => ({
      activePetProfile: {
        ...store.activePetProfile,
        [targetProfile.petType]: petProfileId,
      },
    }));
  },

  fetchPetProfiles: async (customerId: number | null) => {
    if (customerId === null) {
      console.error(`Invalid customerId: ${customerId}`);
      // logged-out is "known-empty"; consumers gating on isCustomerPetsLoaded must stop waiting
      set({ isCustomerPetsLoaded: true });
      get().syncProfileCookies();
      return;
    }

    try {
      const customerPets = await getPets(customerId);
      const customerPetProfiles = customerPets?.map((pet) => fromPetToProfile(pet));

      set((store) => {
        const customerKey = getCustomerKey(store.customerId);

        return {
          // keep all previous profiles and replace the ones reladed to the customer
          petProfiles: unionBy([...customerPetProfiles, ...store.petProfiles], 'id'),
          profilesPerCustomer: {
            ...store.profilesPerCustomer,
            [customerKey]: customerPetProfiles.map((profile) => profile.id),
          },
          isCustomerPetsLoaded: true,
          // the persisted active id may point to a profile the customer does not own
          // (e.g. a guest profile after logging in) - repoint it to an existing profile
          activePetProfile: reconcileActivePetProfile({
            activePetProfile: store.activePetProfile,
            customerPets: customerPetProfiles,
          }),
        } satisfies Partial<RootStore>;
      });
      get().syncProfileCookies();
    } catch (refreshError) {
      console.error('Failed to refresh pets', refreshError);
    }
  },

  // can be called multiple times, by default it selects active on creation, it is not needed during the migration (during account creation)
  savePetProfile: async (petProfile: PetProfile, { selectAfterCreation = true } = {}) => {
    const { fetchPetProfiles, selectPetProfile } = get();
    const { customerId, profilesPerCustomer } = get();
    const customerKey = getCustomerKey(customerId);
    const normalizedPetProfile = normalizePetProfileProteinPreferences(petProfile);

    if (customerKey === DEFAULT_CUSTOMER_ID) {
      set((store) => mergePetProfiles({ store, petProfile: normalizedPetProfile }));

      if (selectAfterCreation) {
        selectPetProfile({ petProfileId: normalizedPetProfile.id });
      }

      get().syncProfileCookies();
      return normalizedPetProfile;
    }

    const payload = toPayloadByPetProfile({
      petProfile: normalizedPetProfile,
      customerId: customerKey,
    });
    const shouldUpdate = profilesPerCustomer[customerKey].includes(normalizedPetProfile.id);

    try {
      const savedPetProfile = shouldUpdate
        ? await updatePet(payload)
        : await createPet(omit(payload, ['id']));

      await fetchPetProfiles(customerId);

      if (selectAfterCreation) {
        selectPetProfile({ petProfileId: savedPetProfile.id });
      }

      return fromPetToProfile(savedPetProfile);
    } catch (error) {
      console.error(
        `Failed to ${shouldUpdate ? 'update' : 'create'} pet profile ${normalizedPetProfile.id}`,
        { payload },
        error
      );
      throw error;
    }
  },

  deletePetProfile: async (petProfile: PetProfile) => {
    const { customerId, activePetProfile, customerPets } = get();
    const customerKey = getCustomerKey(customerId);
    // in case currently active pet profile is being deleted - select another one
    const isCurrentlyActive = activePetProfile[petProfile.petType] === petProfile.id;
    const fallback = petProfile.petType === PetType.Dog ? INITIAL_DOG_STATE : INITIAL_CAT_STATE;
    const replacement =
      customerPets.filter(
        (item) => item.petType === petProfile.petType && item.id !== petProfile.id
      )[0] || fallback;

    if (customerKey === DEFAULT_CUSTOMER_ID) {
      set((store) => {
        return {
          // TODO: maybe it would be worth to do it with compute? but then there is a dependency between active pet profile and current one
          activePetProfile: isCurrentlyActive
            ? { ...store.activePetProfile, [petProfile.petType]: replacement.id }
            : store.activePetProfile,
          petProfiles: store.petProfiles.filter((item) => petProfile.id !== item.id),
          profilesPerCustomer: {
            ...store.profilesPerCustomer,
            [customerKey]: store.profilesPerCustomer[customerKey].filter(
              (id) => petProfile.id !== id
            ),
          },
        } satisfies Partial<RootStore>;
      });
      get().syncProfileCookies();
      return;
    }

    try {
      await deletePet(petProfile.id);
      await get().fetchPetProfiles(customerId);
      set((store) => {
        return {
          activePetProfile: isCurrentlyActive
            ? { ...store.activePetProfile, [petProfile.petType]: replacement.id }
            : store.activePetProfile,
        };
      });
    } catch (error) {
      console.error('Failed to execute action delete pet', error);
    }
  },

  // case when we switch from guest to real customer and migrate guest profiles to BE
  switchWithMigration: async (customerId: number) => {
    const {
      customerPets,
      setCustomerId,
      savePetProfile,
      flushGuest,
      activePetProfile,
      selectPetProfile,
    } = get();
    await setCustomerId({ customerId, refetchProfiles: false }); // switch guest id to a new customer id, no need to fetch pet profiles, since they do not exist

    if (customerPets.length === 0) return;

    for (const petProfile of customerPets) {
      try {
        // during the migration from local to BE profiles we will lost currentPetProfile, because locally generated uuid will not match BE response
        // we compare active profiles ids (for dog and cat) with the request payload, to know that we need to reassign as new active ones after BE response is recieved
        const savedProfile = await savePetProfile(petProfile, { selectAfterCreation: false });
        const isActiveProfile = petProfile.id === activePetProfile[petProfile.petType];
        if (isActiveProfile && savedProfile !== undefined) {
          selectPetProfile({ petProfileId: savedProfile.id });
        }
      } catch {
        // already logged in savePetProfile; keep migrating the remaining profiles
      }
    }

    flushGuest(customerPets);
  },

  flushGuest: (guestProfiles) => {
    set(
      (store) =>
        ({
          petProfiles: store.petProfiles.filter(
            (profile) => !guestProfiles.find((item) => item.id === profile.id)
          ),
          profilesPerCustomer: {
            ...store.profilesPerCustomer,
            [DEFAULT_CUSTOMER_ID]: [],
          },
        }) satisfies Partial<RootStore>
    );
    get().syncProfileCookies();
  },
});
